import { Box, IconButton, Tooltip } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { AssortmentTag } from '@retail/my-assortment/types';
import { getLLDate } from '@retail/utils';
import { CheckIcon, DeleteIcon, NavigateArrowIcon } from '@shared/custom-icons';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { UpdateAssortmentTagMenu } from './UpdateAssortmentTagMenu';
import { FILTER_CONFIG } from '@retail/products/utils';

interface Props {
  assortmentsTag: AssortmentTag[];
  assortmentLink: string;
  isBrandContext: boolean;
  includeSendEvents: boolean;
  deleteTag: (tagId: AssortmentTag) => void;
  updateTag: (
    tag: AssortmentTag,
    field: 'sendEvents' | 'availableToMember' | 'selectableByMembers'
  ) => void;
}

const baseColumnProps: Partial<GridColDef<AssortmentTag>> = {
  flex: 1,
  sortable: false,
};

export function AssortmentsOverview({
  assortmentsTag,
  assortmentLink,
  isBrandContext,
  includeSendEvents,
  deleteTag,
  updateTag,
}: Props) {
  const t = useMyAssortmentTFunction();

  const baseColumns: GridColDef<AssortmentTag>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'id',
        flex: 0,
        width: 100,
        headerName: t('myAssortment.columns.id'),
      },
      {
        ...baseColumnProps,
        field: 'name',
        width: 200,
        headerName: t('myAssortment.columns.name'),
      },
      {
        ...baseColumnProps,
        field: 'count',
        headerName: t('myAssortment.columns.nrOfItems'),
      },
      {
        ...baseColumnProps,
        headerAlign: 'center',
        align: 'center',
        field: 'visibleToMembers',
        headerName: t('myAssortment.columns.showToMember'),
        renderCell: ({ value }) => value && <CheckIcon />,
      },
      {
        ...baseColumnProps,
        headerAlign: 'center',
        align: 'center',
        field: 'selectableByMembers',
        headerName: t('myAssortment.columns.selectableByMembers'),
        renderCell: ({ value }) => value && <CheckIcon />,
      },
      {
        ...baseColumnProps,
        field: 'validFrom',
        headerName: t('myAssortment.columns.validFrom'),
        renderCell: ({ value }) => getLLDate(value),
      },
      {
        ...baseColumnProps,
        field: 'validTo',
        headerName: t('myAssortment.columns.validTo'),
        renderCell: ({ value }) => getLLDate(value) ?? '-',
      },
      {
        ...baseColumnProps,
        field: 'actions',
        headerName: '',
        flex: 0,
        width: isBrandContext ? 130 : 96,
        renderCell: ({ row }) => (
          <>
            {isBrandContext && (
              <UpdateAssortmentTagMenu
                visibleToMembers={row.visibleToMembers}
                selectableByMembers={row.selectableByMembers}
                sendEvents={row.sendEvents}
                updateSendEvents={
                  includeSendEvents ? () => updateTag(row, 'sendEvents') : undefined
                }
                updateVisibleToMembers={() => updateTag(row, 'availableToMember')}
                updateSelectableByMembers={() => updateTag(row, 'selectableByMembers')}
              />
            )}

            {(isBrandContext || row.tagLocation !== 'BRAND') && (
              <IconButton onClick={() => deleteTag(row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            <Tooltip title={t('myAssortment.actions.goToAssortment')}>
              <IconButton
                component={Link}
                to={{
                  pathname: assortmentLink,
                  search: `?${FILTER_CONFIG.subAssortmentIds.urlKey}=${row.id}`,
                }}
              >
                <NavigateArrowIcon />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
    ],
    [assortmentLink, deleteTag, includeSendEvents, isBrandContext, t, updateTag]
  );

  const columns: GridColDef<AssortmentTag>[] = useMemo(
    () =>
      includeSendEvents
        ? [
            ...baseColumns.slice(0, 5),
            {
              ...baseColumnProps,
              headerAlign: 'center',
              align: 'center',
              field: 'sendEvents',
              headerName: t('myAssortment.columns.sendEvents'),
              renderCell: ({ value }) => value && <CheckIcon />,
            },
            ...baseColumns.slice(5),
          ]
        : baseColumns,
    [baseColumns, includeSendEvents, t]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium
          rows={assortmentsTag}
          getRowId={(row) => row.name}
          columns={columns}
          sx={{ '& .MuiDataGrid-cell': { py: 1.5 } }}
          getRowHeight={() => 'auto'}
          columnVisibilityModel={{
            visibleToMembers: isBrandContext,
            selectableByMembers: isBrandContext,
          }}
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooter
          initialState={{ pinnedColumns: { right: ['actions'] } }}
        />
      </Box>
    </Box>
  );
}
